import { FC, Fragment, useState } from "react";
import React from "react";
import { ChangeNames } from "../../clients/FirebaseClient/types";
import { useFormModel } from "../core/FormModelHelpes";
import { BoxedInputWithIconAndOptionalSwitch } from "../core/BoxedInputWithIconAndOptionalSwitch";
import { CalculatorIcon, ChargingStationIcon, DeleteIcon } from "../core/icons/Icons";
import { NumberInput } from "../core/NumberField";
import { useStyles } from "../../useStyles";
import { Grid, IconButton, InputLabel } from "@material-ui/core";
import { Stepper } from "../core/Stepper";
import { MenuItem, Select } from "../core/Select";
import { Button } from "../core/Button";
import { sumUpChargingStationPower } from "../../utils";

const ValidFixedValues = [3.7, 11, 22, 50];
const formatter = new Intl.NumberFormat("de-DE");

const selectionItems = (amount: number | null): { value: number | null; label: string }[] => {
    return [
        ...(amount == null
            ? [
                  {
                      value: null,
                      label: "0 kW",
                  },
              ]
            : []),
        ...ValidFixedValues.map((v) => ({
            value: v,
            label: `${formatter.format(v)} kW`,
        })),
        {
            value: 151,
            label: "Abweichender Wert",
        },
    ];
};
const selectedItemFromNumber = (x: number | null): string => {
    if (x == null) return "0 kW";
    if (ValidFixedValues.includes(x)) return `${formatter.format(x)} kW`;
    return "Abweichender Wert";
};

export const ChargerChargingStationsForm: FC = () => {
    const classes = useStyles();
    const { values, setValues } = useFormModel<ChangeNames.CHARGER_CHARGING_STATIONS>();

    const [focusedIndex, setFocusedIndex] = useState<string | null>();
    const [intermediateValue, setIntermediateValue] = useState<number | null>(0);

    return (
        <Fragment>
            {values.chargerChargingStations.value.map((chargingStation, idx) => (
                <BoxedInputWithIconAndOptionalSwitch
                    key={idx}
                    icon={<ChargingStationIcon />}
                    content={
                        <Fragment key={`fragment-${idx}`}>
                            {values.chargerChargingStations.value.length > 1 ? (
                                <IconButton
                                    style={{ position: "absolute", top: 24, right: 22 }}
                                    onClick={() => {
                                        setValues((values) => {
                                            values.chargerChargingStations.value.splice(idx, 1);
                                        });
                                    }}
                                >
                                    <DeleteIcon size="size24" />
                                </IconButton>
                            ) : (
                                <Fragment />
                            )}
                            <p className={classes.label} style={{ marginBottom: 8, marginTop: 12 }}>
                                Ladeeinrichtung der gleichen Bauart
                            </p>
                            <p
                                className={classes.labledTextfieldLabel}
                                style={{ marginBottom: 12 }}
                            >
                                Wie viele Ladeinrichtungen dieser Bauart haben Sie geplant?
                            </p>
                            <Stepper
                                allowNegative={false}
                                value={chargingStation.count || 0}
                                onValueChange={(v) =>
                                    setValues((values) => {
                                        values.chargerChargingStations.value[idx].count =
                                            v.floatValue ||
                                            values.chargerChargingStations.value[idx].count;
                                    })
                                }
                            />
                            <p
                                className={classes.labledTextfieldLabel}
                                style={{ marginBottom: 12 }}
                            >
                                Wie viele Steckvorrichtungen (Ladepunkte) hat Ihre Ladeeinrichtung?
                            </p>
                            <Stepper
                                allowNegative={false}
                                value={chargingStation.chargingPoints.length}
                                onValueChange={(v) => {
                                    setValues((values) => {
                                        const lenDiff =
                                            values.chargerChargingStations.value[idx].chargingPoints
                                                .length - (v.floatValue || 0);
                                        if (lenDiff > 0) {
                                            for (let index = 0; index < lenDiff; index++) {
                                                values.chargerChargingStations.value[
                                                    idx
                                                ].chargingPoints.pop();
                                            }
                                        } else if (lenDiff < 0) {
                                            for (
                                                let index = 0;
                                                index < Math.abs(lenDiff);
                                                index++
                                            ) {
                                                values.chargerChargingStations.value[
                                                    idx
                                                ].chargingPoints.push({
                                                    value: {
                                                        amount: null,
                                                        precision: 0,
                                                        unit: "kW",
                                                    },
                                                });
                                            }
                                        }
                                    });
                                }}
                            />
                            {chargingStation.chargingPoints.map((chargingPoint, iidx) => {
                                return (
                                    <Grid
                                        key={`div-${idx}-${iidx}`}
                                        style={{ display: "flex" }}
                                        spacing={2}
                                        container
                                        alignItems="flex-end"
                                    >
                                        <Grid item xs={12} sm={6}>
                                            <InputLabel
                                                className={
                                                    chargingPoint.error
                                                        ? classes.labledTextfieldLabelError
                                                        : classes.labledTextfieldLabel
                                                }
                                                style={{ marginTop: 24 }}
                                            >
                                                Leistung am Ladepunkt&nbsp;{iidx + 1}
                                            </InputLabel>
                                            <Select
                                                key={`select-${iidx}`}
                                                style={{ width: "100%" }}
                                                value={selectedItemFromNumber(
                                                    chargingPoint.value.amount,
                                                )}
                                                onChange={(v) => {
                                                    setValues((values) => {
                                                        const newValue = v.target.value;
                                                        if (newValue === "0 kW") {
                                                            values.chargerChargingStations.value[
                                                                idx
                                                            ].chargingPoints[iidx].value.amount =
                                                                null;
                                                        } else if (newValue === ">150 kW") {
                                                            values.chargerChargingStations.value[
                                                                idx
                                                            ].chargingPoints[iidx].value.amount = 0;
                                                        } else {
                                                            values.chargerChargingStations.value[
                                                                idx
                                                            ].chargingPoints[iidx].value.amount =
                                                                newValue
                                                                    ? parseFloat(
                                                                          v.target.value.replace(
                                                                              ",",
                                                                              ".",
                                                                          ),
                                                                      )
                                                                    : null;
                                                        }
                                                    });
                                                }}
                                                error={!!chargingPoint.error}
                                            >
                                                {selectionItems(chargingPoint.value.amount).map(
                                                    (selectableItem) => (
                                                        <MenuItem
                                                            value={selectableItem.label}
                                                            key={selectableItem.value || undefined}
                                                        >
                                                            {selectableItem.label}
                                                        </MenuItem>
                                                    ),
                                                )}
                                            </Select>
                                        </Grid>

                                        {chargingPoint.value.amount !== null &&
                                        !ValidFixedValues.includes(
                                            chargingPoint.value.amount || -1,
                                        ) ? (
                                            <Grid item xs={12} sm={6}>
                                                <InputLabel
                                                    className={
                                                        chargingPoint.error
                                                            ? classes.labledTextfieldLabelError
                                                            : classes.labledTextfieldLabel
                                                    }
                                                    style={{ marginTop: 24 }}
                                                >
                                                    Leistung
                                                </InputLabel>
                                                <NumberInput
                                                    key={`input-${iidx}`}
                                                    className={classes.textField}
                                                    style={{ width: "100%" }}
                                                    fixedDecimalScale={false}
                                                    allowLeadingZeros={false}
                                                    decimalScale={1}
                                                    isNumericString={false}
                                                    allowNegative={false}
                                                    thousandSeparator="."
                                                    decimalSeparator=","
                                                    adornment={chargingPoint.value.unit}
                                                    value={
                                                        focusedIndex === `${idx}:${iidx}`
                                                            ? intermediateValue || ""
                                                            : chargingPoint.value.amount || ""
                                                    }
                                                    placeholder="0"
                                                    onValueChange={(v) => {
                                                        setIntermediateValue(v.floatValue || 0);
                                                    }}
                                                    onFocus={() => {
                                                        setIntermediateValue(
                                                            chargingPoint.value.amount,
                                                        );
                                                        setFocusedIndex(`${idx}:${iidx}`);
                                                        console.log(`focused ${idx}:${iidx}`);
                                                    }}
                                                    onBlur={() => {
                                                        setValues((values) => {
                                                            values.chargerChargingStations.value[
                                                                idx
                                                            ].chargingPoints[iidx].value.amount =
                                                                intermediateValue;
                                                        });
                                                        setIntermediateValue(0);
                                                        setFocusedIndex(null);
                                                        console.log(`blur ${idx}:${iidx}`);
                                                    }}
                                                    error={!!chargingPoint.error}
                                                />
                                            </Grid>
                                        ) : (
                                            <Fragment />
                                        )}
                                    </Grid>
                                );
                            })}
                            <div style={{ height: 12 }} />
                        </Fragment>
                    }
                />
            ))}
            <Button
                mode="action"
                style={{ marginTop: 32, marginBottom: 14 }}
                onClick={() => {
                    setValues((values) => {
                        values.chargerChargingStations.value.push({
                            count: 1,
                            chargingPoints: [
                                {
                                    value: {
                                        amount: null,
                                        precision: 0,
                                        unit: "kW",
                                    },
                                },
                            ],
                        });
                    });
                }}
            >
                Weitere Ladeeinrichtung einer anderen Bauart hinzufügen
            </Button>
            <BoxedInputWithIconAndOptionalSwitch
                icon={<CalculatorIcon />}
                content={
                    <>
                        <p className={classes.label} style={{ marginTop: 12 }}>
                            Gesamtleistung aller Ladeeinrichtungen
                        </p>
                        <InputLabel className={classes.labledTextfieldLabel}>
                            Ermittelte Gesamtleistung (kW = kVA)
                        </InputLabel>
                        <NumberInput
                            className={classes.textField}
                            fixedDecimalScale={false}
                            allowLeadingZeros={false}
                            decimalScale={1}
                            isNumericString={false}
                            allowNegative={false}
                            thousandSeparator="."
                            decimalSeparator=","
                            adornment={
                                values.chargerChargingStations.value[0].chargingPoints[0].value.unit
                            }
                            value={sumUpChargingStationPower(values.chargerChargingStations.value)}
                            disabled
                        />
                        <div style={{ height: 12 }} />
                    </>
                }
            />
        </Fragment>
    );
};
